// src/hooks/useForceLogout.ts
// Hook for handling SSO force logout via WebSocket

import { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authSocketService, ForceLogoutPayload } from '../services/authSocketService';
import { clearAuthState, getAuthToken } from '../services/authService';

interface UseForceLogoutOptions {
  /** Callback to clear user state in the app */
  onLogout?: () => void;
  /** Whether the user is currently authenticated */
  isAuthenticated: boolean;
}

/**
 * Hook that connects to the auth WebSocket and handles force logout events.
 *
 * When an IdP-initiated SLO occurs:
 * 1. IdP sends LogoutRequest to /auth/saml/logout
 * 2. Backend revokes tokens + broadcasts force_logout via PubSub
 * 3. This hook receives the WebSocket event
 * 4. App clears tokens and redirects to login
 */
export function useForceLogout({ onLogout, isAuthenticated }: UseForceLogoutOptions): void {
  const navigate = useNavigate();
  const isHandlingLogoutRef = useRef(false);

  const handleForceLogout = useCallback(
    (payload: ForceLogoutPayload) => {
      // Prevent multiple logout handlers from running
      if (isHandlingLogoutRef.current) {
        return;
      }
      isHandlingLogoutRef.current = true;

      console.log('[useForceLogout] Force logout received:', payload.reason);

      // Disconnect the socket first
      authSocketService.disconnect();

      // Clear all auth state (cookies, localStorage, axios headers)
      clearAuthState();

      // Call the app's logout callback if provided
      if (onLogout) {
        onLogout();
      }

      // Redirect to login with a message
      const logoutReason = getLogoutReasonMessage(payload.reason);
      navigate('/login', {
        replace: true,
        state: {
          message: logoutReason,
          fromForceLogout: true
        }
      });

      // Reset the flag after a short delay
      setTimeout(() => {
        isHandlingLogoutRef.current = false;
      }, 1000);
    },
    [navigate, onLogout]
  );

  useEffect(() => {
    // Only connect when authenticated and we have a token
    const token = getAuthToken();
    if (!isAuthenticated || !token) {
      // Ensure we're disconnected when not authenticated
      authSocketService.disconnect();
      return;
    }

    // Connect to the auth socket
    authSocketService.connect(handleForceLogout);

    // Cleanup on unmount or when auth state changes
    return () => {
      authSocketService.disconnect();
    };
  }, [isAuthenticated, handleForceLogout]);

  // Also listen for storage events for cross-tab logout
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      // If user was removed from localStorage in another tab, logout here too
      if (event.key === 'user' && event.newValue === null && isAuthenticated) {
        console.log('[useForceLogout] Cross-tab logout detected');
        handleForceLogout({
          reason: 'logged_out_in_another_tab',
          timestamp: new Date().toISOString(),
        });
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [isAuthenticated, handleForceLogout]);
}

/**
 * Get a user-friendly message for the logout reason
 */
function getLogoutReasonMessage(reason: string): string {
  switch (reason) {
    case 'idp_logout':
    case 'slo_request':
      return 'You have been signed out by your organization.';
    case 'session_expired':
      return 'Your session has expired. Please sign in again.';
    case 'token_revoked':
      return 'Your session was ended. Please sign in again.';
    case 'logged_out_in_another_tab':
      return 'You were signed out in another tab.';
    case 'admin_action':
      return 'Your session was ended by an administrator.';
    default:
      return 'You have been signed out. Please sign in again.';
  }
}

export default useForceLogout;
